/**
 * Flashroom configuration — the single source of truth.
 *
 * `CONFIG` is the one object the whole app imports (UI + workers). Change a
 * value here and it changes everywhere; nothing product-specific is hardcoded
 * elsewhere. Shape is fixed by GRAPH.md "Configuration".
 */
import { sliceVisible, visibleLength } from "./language";

export const CONFIG = {
  APP_NAME: "Flashroom",

  // The all-Cloudflare R2/HLS video pipeline (nodes V0–V9) — the SOLE video
  // provider (Cloudflare Stream removed at the V9 cutover). The single source of
  // truth for the transcode ladder + segment/preset the container is LOCKED to by
  // the V0 benchmark (notes/v0-benchmark-results.md) — the orchestration Workflow
  // (V3) and the R2HlsVideo seam (V4) both import these, so the encode contract
  // lives in exactly one place.
  VIDEO: {
    provider: "r2-hls",
    // R2 bucket that holds the source video + its written HLS ladder.
    bucket: "flashroom-video",
    // Custom domain fronting the bucket for free-egress CDN delivery (wired at V5;
    // until then getStatus returns the bare R2 key path).
    cdnHost: "videos.flashsuite.net",
    // The 5-rung ABR ladder (output height + target video kbps). Capped at the
    // source height at encode time (never upscaled).
    ladder: [
      { height: 1080, kbps: 4800 },
      { height: 720, kbps: 2500 },
      { height: 480, kbps: 1100 },
      { height: 360, kbps: 600 },
      { height: 240, kbps: 300 },
    ],
    // Segment length in seconds == the keyframe interval (fMP4 HLS).
    segmentSec: 4,
    // libx264 preset locked by V0.
    preset: "veryfast",
    // Container instance type locked by V0 (4 vCPU / 12 GiB / 20 GB disk).
    instanceType: "standard-4",
    // Chunk-parallel unit (V7): keyframe-aligned slice length per container.
    // 45 s snaps down to 44 (eleven 4 s segments), so an 85-minute talk plans
    // 116 chunks and fits in one wave of containers.
    chunkSec: 45,
  },

  // The four timed interaction types placed on a webinar timeline.
  INTERACTIONS: ["poll", "question", "reaction", "handout"],

  // An automation starts when a tag is added or removed on a person, and
  // nothing else: everything a person does is a tag (shared/tags.ts), so the
  // funnel is composed by the host from Wait and Branch, not baked in here.
  TRIGGERS: ["tag_added", "tag_removed"],

  // Automation actions. `fact` writes a tag/detail on the contact (so automations
  // can chain: one's output is another's trigger). Ending a run early is not an
  // action: it is the automation's own stop rule, read before every step.
  ACTIONS: ["email", "room", "fact", "webhook", "notify", "wait", "branch"],

  // Webinar schedule modes.
  SCHEDULE_MODES: ["on_demand", "recurring", "one_time", "live"],

  // "The Ledger" design identity. Concrete CSS tokens live in the design system
  // (T2); these are the brand-level constants the config pins.
  THEME: {
    mode: "dark|light",
    accent: "wine #7a2338",
    brass: "#b0873a",
    font: "Plus Jakarta Sans",
  },
} as const;

/**
 * The models, as OpenRouter names them. Every model call goes through
 * `workers/src/ai/openrouter.ts`; swap a line here to move a capability.
 *
 * The read runs on DeepSeek V4 Flash 0731 (the GA release, 1M context, JSON
 * schema output on the hosts we prefer); the pages on GLM 5.3 Flash. Verified
 * on OpenRouter's live model and endpoint pages (2026-08-28):
 * https://openrouter.ai/deepseek/deepseek-v4-flash-0731
 * https://openrouter.ai/z-ai/glm-5.3-flash
 */
export const AI_MODEL = "deepseek/deepseek-v4-flash-0731";

/**
 * Reasoning off by default: verified faster/cheaper with equal quality + valid
 * grounded output on our tasks. Flip to true to enable globally; any call can
 * override per-request. Off is `reasoning.effort: "none"` on OpenRouter.
 */
export const AI_REASONING = false;

/** Chat edits to one part of a page: fast and cheap. A whole-page change goes to the design model below. */
export const PAGE_MODEL = "z-ai/glm-5.3-flash";

/**
 * The first design of a page, its fixes and a whole-page change: Opus alone, as designer and engineer. $4 per million
 * tokens in and $20 out (OpenRouter's model page, 2026-10-27); the wall sits above that so a host
 * that quotes a little more is not refused.
 */
export const PAGE_DESIGN_MODEL = "anthropic/claude-opus-5.5";
export const PAGE_DESIGN_MAX_PRICE = { prompt: 6, completion: 30 } as const;
/** The most one design call may write. The thinking and the page share it and medium effort may spend half on thinking; a page is 11 to 13k tokens, and at 24k a draft was cut off mid-page (2026-10-27, measured on the first real run). */
export const PAGE_DESIGN_MAX_TOKENS = 32_000;

/**
 * How OpenRouter picks the host for a call: the fastest one that is up right
 * now, among hosts that do not collect the data. A fixed order was tried first
 * (Baseten, the fastest on paper) and it was rate-limited upstream for both
 * models on the day, so the live pick beats a list. Verified against the
 * provider-routing docs (`provider.sort`), 2026-08-28.
 */
export const AI_SORT = "throughput";

/**
 * The most a host may charge, in dollars per million tokens, for each model.
 * The fastest host wins only under this wall; above it OpenRouter refuses the
 * call rather than paying, so a host raising its price can never surprise the
 * bill (`provider.max_price`, the one routing field that refuses instead of
 * deprioritising). Set from the endpoint prices on 2026-08-28 with a little
 * headroom: the read's normal hosts are $0.08 to $0.14 in and $0.18 to $0.28
 * out; the page model's $0.075 to $0.15 in and $0.25 to $0.50 out.
 */
export const AI_MAX_PRICE = { prompt: 0.16, completion: 0.32 } as const;
export const PAGE_MAX_PRICE = { prompt: 0.18, completion: 0.6 } as const;

// ── Typed boundaries derived from the config (import these, don't re-declare) ──
export type InteractionType = (typeof CONFIG.INTERACTIONS)[number];
export type TriggerType = (typeof CONFIG.TRIGGERS)[number];

/** The triggers a board follow-up may run on: the same two as everything else. */
export const BOARD_TRIGGERS: readonly TriggerType[] = CONFIG.TRIGGERS;

/** Is this a trigger a board follow-up is allowed to run on? */
export function isBoardTrigger(type: string): type is TriggerType {
  return (BOARD_TRIGGERS as readonly string[]).includes(type);
}
export type ActionType = (typeof CONFIG.ACTIONS)[number];
export type ScheduleMode = (typeof CONFIG.SCHEDULE_MODES)[number];

// -- First-Webinar Studio: the harness step machine --
/**
 * The register-page harness steps IN THE ORDER THE STUDIO WALKS THEM. This list
 * is the single source of truth: the server advances `webinars.harness_step`
 * along it and the Studio resumes by it, so the two can never disagree about
 * what the host does next.
 */
export const HARNESS_STEP_ORDER = ["titles", "promise", "moments", "qa", "offer"] as const;

/**
 * Parked here once the last page step is committed: the register page is
 * finished, automations and publishing are not. Distinct from "done", which
 * finalize alone writes, so an unpublished draft never looks published.
 */
export const HARNESS_STEP_REVIEW = "review";
/** Written by finalize and by nothing else: the webinar is live. */
export const HARNESS_STEP_DONE = "done";

// ── Studio copy limits ───────────────────────────────────────────────
// A host got back a 14-word title on three lines and a 60-word description.
// Register pages read as headlines, not paragraphs, so the limits are part of the
// contract: the prompt states them, the JSON schema caps the fields, the parser
// enforces them, and the Studio shows the same counters off these exact numbers.
//
// The limits are CHARACTER counts, and only character counts. That is what people
// expect from a length limit and it is the only thing the page layout actually
// cares about: two titles of the same word count can wrap differently, while two
// titles of the same character count do not.

// -- Publishing a recording: the one place the video guard is written --
/**
 * The `webinars.video_state` values that mean the recording is not playable yet.
 * Publishing points the funnel at the watch page, so every publish path (the
 * manual one, the Studio harness and the Producer board) holds a recording to the
 * SAME bar and tells the host the SAME thing. One list, one set of words.
 */
export const PROCESSING_VIDEO_STATES: readonly string[] = [
  "uploading",
  "probing",
  "transcoding",
  "assembling",
  "poster",
  "transcribing",
];
/** The only state a published webinar may sit in: the encode finished. */
export const VIDEO_READY = "ready";
/** Still encoding: worth waiting for. */
export const VIDEO_PROCESSING_MSG = "Your video is still processing. Give it a minute and try again.";
/** The encode failed: the upload has to happen again. */
export const VIDEO_ERROR_MSG = "We couldn't process that video. Upload it again.";
/** No recording at all, or one that never landed. */
export const VIDEO_MISSING_MSG = "Add a recording before publishing.";

/**
 * The most one recording or picked video may weigh. Notes go straight to storage
 * in parts, so the ceiling is a product decision rather than a technical one: ten
 * gigabytes is a long camera file, and an upload with no ceiling fails somewhere
 * the host cannot see. Checked in the browser for the words and on the reserve
 * route for the rule, because only the second one is a gate.
 */
export const MAX_NOTE_BYTES = 10 * 1024 * 1024 * 1024;
/** What the host is told when a recording or a video is over that. */
export const NOTE_TOO_BIG_MSG = "That video is over 10 GB. Trim it, or record a shorter one.";
/**
 * A video note at least this long is made the way a webinar's recording is: cut
 * into chunks, encoded on many containers at once, and played as an adaptive
 * stream (`hls/<id>/master.m3u8`). Why five minutes: one container encodes at
 * about half real time, so a short camera reply is ready in a minute as one file,
 * while an hour of footage would keep the host waiting two.
 */
export const NOTE_STREAM_MIN_SEC = 5 * 60;

/** A title is a headline. Longer than this and it wraps to three lines. */
export const TITLE_MAX_CHARS = 60;
/**
 * The line under the title. Host-facing copy calls this field the DESCRIPTION;
 * the internal step key and the block type are still `promise`.
 */
export const PROMISE_MAX_CHARS = 160;
/** The supporting text under the description, when the model still produces one. */
export const DESCRIPTION_MAX_CHARS = 220;

/** Split into sentences, keeping the terminator on each. */
export function splitSentences(text: string): string[] {
  return text
    .trim()
    .split(/(?<=[.!?])\s+|(?<=[。！？｡؟۔।॥։])\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * The LENGTH cap a title must respect. Applied to a host-typed title too, where
 * the style rules below deliberately are not: a host may punctuate their own
 * headline however they like, they just cannot write a paragraph.
 */
export function titleWithinCaps(title: string): boolean {
  const t = title.trim();
  return t.length > 0 && visibleLength(t) <= TITLE_MAX_CHARS;
}

/**
 * Everything a MODEL-written title must respect: the cap, plus no colon-subtitle
 * construction ("Big Idea: The Complete Guide") and no quotation marks, both of
 * which are the model padding a headline out.
 */
export function titleWithinLimits(title: string): boolean {
  const t = title.trim();
  if (!titleWithinCaps(t)) return false;
  if (t.includes(":")) return false;
  // Double quotes only. An apostrophe is ordinary English ("Today's Market") and
  // rejecting it would throw away good headlines.
  if (/["“”]/.test(t)) return false;
  return true;
}

/** The cap the description must respect, for both the model and a host edit. */
export function promiseWithinCaps(promise: string): boolean {
  const p = promise.trim();
  return p.length > 0 && visibleLength(p) <= PROMISE_MAX_CHARS;
}

/**
 * Cut `text` down to `maxChars` on a SENTENCE boundary, so trimmed copy still
 * reads as finished writing rather than a dangling clause. The cap is characters;
 * whole sentences are just the nicest place to make the cut. Falls back to a word
 * boundary only when the first sentence alone is already too long.
 */
export function trimToSentences(text: string, maxChars: number): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  let out = "";
  for (const sentence of splitSentences(trimmed)) {
    const candidate = out ? `${out} ${sentence}` : sentence;
    if (visibleLength(candidate) > maxChars) break;
    out = candidate;
  }
  if (out) return out;
  // One very long sentence: cut at the last whole word that fits.
  const head = sliceVisible(trimmed, maxChars);
  const lastSpace = head.lastIndexOf(" ");
  return (lastSpace > 0 ? head.slice(0, lastSpace) : head).replace(/[,;:\s]+$/, "");
}

/**
 * The plain-words "when it runs" line for an automation, from its trigger type.
 * Exhaustive over `CONFIG.TRIGGERS` (the `Record<TriggerType, string>` makes
 * TypeScript say so), because a trigger with no line here reads as the useless
 * "Runs automatically" everywhere it is shown.
 */
const TRIGGER_DETAIL: Record<TriggerType, string> = {
  tag_added: "Runs when someone gets a tag",
  tag_removed: "Runs when someone loses a tag",
};

/** "When someone gets the tag registered", from a stored trigger of any age. */
export function triggerDetail(trigger: { type?: string; value?: string } | string | null | undefined): string {
  if (!trigger) return "Runs automatically";
  const t = typeof trigger === "string" ? { type: trigger } : trigger;
  const verb = t.type === "tag_removed" ? "loses the tag" : "gets the tag";
  return t.value ? `When someone ${verb} ${t.value}` : TRIGGER_DETAIL[t.type as TriggerType] || "Runs automatically";
}

export const APP_NAME = CONFIG.APP_NAME;

/** The locked R2/HLS video pipeline settings (ladder/segment/preset/instance). */
export const VIDEO = CONFIG.VIDEO;
